import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, KeyRound, ListChecks } from "lucide-react";
import SecurityReminder from "@/components/SecurityReminder";

const DEFAULT_CHECKLIST = ["Beds changed", "Bathroom cleaned", "Kitchen cleaned", "Floors cleaned",
  "Towels replaced", "Bins emptied", "Toiletries restocked", "Windows checked", "Final inspection completed"];

const emptyForm = {
  name: "", address_line1: "", address_city: "", address_postcode: "",
  bedrooms: 1, bathrooms: 1, sleeps: 2, size_approx: "", parking_info: "", cleaner_notes: "",
  estimated_duration_hours: "",
  access: { key_safe_location: "", lockbox_code: "", smart_lock_instructions: "", alarm_instructions: "", entry_notes: "" },
  checklist: DEFAULT_CHECKLIST.map((label, i) => ({ id: `chk_${i}`, label })),
};

export default function PropertyForm({ property, onSaved, onCancel }) {
  const [form, setForm] = useState(property ? {
    name: property.name || "",
    address_line1: property.address?.line1 || "",
    address_city: property.address?.city || "",
    address_postcode: property.address?.postcode || "",
    bedrooms: property.bedrooms ?? 1, bathrooms: property.bathrooms ?? 1, sleeps: property.sleeps ?? 2,
    size_approx: property.size_approx || "", parking_info: property.parking_info || "",
    cleaner_notes: property.cleaner_notes || "",
    estimated_duration_hours: property.estimated_duration_hours ?? "",
    access: { key_safe_location: "", lockbox_code: "", smart_lock_instructions: "", alarm_instructions: "", entry_notes: "", ...(property.access || {}) },
    checklist: (property.checklist?.length ? property.checklist : emptyForm.checklist),
  } : emptyForm);
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState("");

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updAccess = (k, v) => setForm((f) => ({ ...f, access: { ...f.access, [k]: v } }));
  const hasKeyInfo = !!(form.access.key_safe_location || form.access.lockbox_code);

  const addItem = () => {
    if (!newItem.trim()) return;
    upd("checklist", [...form.checklist, { id: `chk_new_${Date.now()}`, label: newItem.trim() }]);
    setNewItem("");
  };

  const save = async () => {
    if (!form.name.trim() || !form.address_line1.trim() || !form.address_city.trim() || !form.address_postcode.trim()) {
      toast.error("Please add a property name and full address."); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        bedrooms: Number(form.bedrooms), bathrooms: Number(form.bathrooms), sleeps: Number(form.sleeps),
        estimated_duration_hours: form.estimated_duration_hours === "" ? null : Number(form.estimated_duration_hours),
      };
      const { data } = property
        ? await api.put(`/properties/${property.property_id}`, payload)
        : await api.post("/properties", payload);
      toast.success(property ? "Property updated" : "Property added");
      if (hasKeyInfo) toast.warning("Security Reminder — remember to change your key location or lockbox code when you start with a new cleaner.");
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-card rounded-2xl border border-primary/5 p-5 sm:p-6 space-y-6" data-testid="property-form">
      <div>
        <h3 className="font-display text-lg font-semibold">{property ? "Edit property" : "Add a property"}</h3>
        <p className="text-sm text-muted-foreground mt-1">Used to pre-fill bookings and brief your cleaner accurately.</p>
      </div>

      <div className="space-y-4">
        <F label="Property name"><input value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="e.g. Seaview Apartment" className="pinput" data-testid="property-name" /></F>
        <F label="Address line 1"><input value={form.address_line1} onChange={(e) => upd("address_line1", e.target.value)} className="pinput" data-testid="property-address1" /></F>
        <div className="grid sm:grid-cols-2 gap-4">
          <F label="City / town"><input value={form.address_city} onChange={(e) => upd("address_city", e.target.value)} className="pinput" data-testid="property-city" /></F>
          <F label="Postcode"><input value={form.address_postcode} onChange={(e) => upd("address_postcode", e.target.value)} className="pinput" data-testid="property-postcode" /></F>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <F label="Bedrooms"><input type="number" min="0" value={form.bedrooms} onChange={(e) => upd("bedrooms", e.target.value)} className="pinput" data-testid="property-bedrooms" /></F>
          <F label="Bathrooms"><input type="number" min="0" value={form.bathrooms} onChange={(e) => upd("bathrooms", e.target.value)} className="pinput" data-testid="property-bathrooms" /></F>
          <F label="Sleeps"><input type="number" min="1" value={form.sleeps} onChange={(e) => upd("sleeps", e.target.value)} className="pinput" data-testid="property-sleeps" /></F>
          <F label="Est. clean (hrs)"><input type="number" min="1" step="0.5" value={form.estimated_duration_hours} onChange={(e) => upd("estimated_duration_hours", e.target.value)} placeholder="Optional" className="pinput" data-testid="property-duration" /></F>
        </div>
        <F label="Approximate size (optional)"><input value={form.size_approx} onChange={(e) => upd("size_approx", e.target.value)} placeholder="e.g. 85 m² / 2 floors" className="pinput" data-testid="property-size" /></F>
        <F label="Parking information"><input value={form.parking_info} onChange={(e) => upd("parking_info", e.target.value)} placeholder="e.g. Free driveway space, permit not required" className="pinput" data-testid="property-parking" /></F>
        <F label="Notes for cleaners"><textarea rows={3} value={form.cleaner_notes} onChange={(e) => upd("cleaner_notes", e.target.value)} placeholder="e.g. Linen cupboard is in the hallway. Bins go out on Tuesdays." className="pinput" data-testid="property-notes" /></F>
      </div>

      <div className="border-t border-border pt-5">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-accent" />
          <h4 className="font-semibold text-sm">Contactless access</h4>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Saved privately. Only your assigned cleaner can see these details, and only while the booking is confirmed — they are hidden again as soon as the job is complete.
        </p>
        <div className="space-y-4 mt-4">
          <F label="Key safe location"><input value={form.access.key_safe_location} onChange={(e) => updAccess("key_safe_location", e.target.value)} placeholder="e.g. Black safe on wall, left of the front door" className="pinput" data-testid="access-keysafe" /></F>
          <F label="Lockbox code"><input value={form.access.lockbox_code} onChange={(e) => updAccess("lockbox_code", e.target.value)} className="pinput" data-testid="access-lockbox" /></F>
          <F label="Smart lock instructions"><textarea rows={2} value={form.access.smart_lock_instructions} onChange={(e) => updAccess("smart_lock_instructions", e.target.value)} className="pinput" data-testid="access-smartlock" /></F>
          <F label="Alarm instructions"><textarea rows={2} value={form.access.alarm_instructions} onChange={(e) => updAccess("alarm_instructions", e.target.value)} className="pinput" data-testid="access-alarm" /></F>
          <F label="Entry notes"><textarea rows={2} value={form.access.entry_notes} onChange={(e) => updAccess("entry_notes", e.target.value)} placeholder="e.g. Side gate sticks — lift slightly" className="pinput" data-testid="access-entry" /></F>
        </div>
        {hasKeyInfo && <SecurityReminder className="mt-4" />}
      </div>

      <div className="border-t border-border pt-5">
        <div className="flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-accent" />
          <h4 className="font-semibold text-sm">Turnover checklist</h4>
        </div>
        <p className="text-sm text-muted-foreground mt-1">Your cleaner ticks these off during the clean. Edit or add your own.</p>
        <div className="mt-4 space-y-2" data-testid="checklist-editor">
          {form.checklist.map((it, i) => (
            <div key={it.id} className="flex gap-2 items-center">
              <input value={it.label} onChange={(e) => upd("checklist", form.checklist.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x))} className="pinput flex-1" data-testid={`checklist-item-${i}`} />
              <button onClick={() => upd("checklist", form.checklist.filter((_, xi) => xi !== i))} className="p-2 text-muted-foreground hover:text-destructive" data-testid={`checklist-remove-${i}`} aria-label="Remove item"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <input value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addItem())} placeholder="Add a task" className="pinput flex-1" data-testid="checklist-new" />
          <button onClick={addItem} className="px-4 rounded-xl bg-primary text-primary-foreground font-medium inline-flex items-center gap-1 hover:bg-primary/90 transition-colors" data-testid="checklist-add"><Plus className="h-4 w-4" /> Add</button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <button onClick={save} disabled={saving} className="flex-1 py-3 rounded-full bg-accent text-white font-medium hover:bg-accent/90 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="property-save">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} {property ? "Save changes" : "Add property"}
        </button>
        <button onClick={onCancel} className="flex-1 py-3 rounded-full border border-border font-medium hover:bg-secondary transition-colors" data-testid="property-cancel">Cancel</button>
      </div>

      <style>{`.pinput{width:100%;margin-top:0.25rem;padding:0.625rem 0.75rem;border-radius:0.75rem;border:1px solid hsl(var(--border));background:hsl(var(--background));outline:none}.pinput:focus{border-color:hsl(var(--accent))}`}</style>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="text-sm font-medium">{label}</label>{children}</div>;
}
